import type { PrefetchContext, PrefetchedDataProps } from '#src/shared/prefetch';

import { useQueryString } from '#src/app/shared/hooks/location';
import type { MergedState } from '#src/app/shared/hooks/state';
import { useMergedStates } from '#src/app/shared/hooks/state';
import { getQueryStringSingleValue } from '#src/app/shared/utils/query';

import { prefetchLocalPageDataFromApi, useLocalPageDataFromApi } from './pageData';
import {
	prefetchLocalPagePaginatedDecisionMakersDataFromApi,
	useLocalPagePaginatedDecisionMakersDataFromApi,
} from './pageDecisionMakersPaginatedData';
import {
	prefetchLocalPagePaginatedPetitionUpdatesDataFromApi,
	useLocalPagePaginatedPetitionUpdatesDataFromApi,
} from './pagePetitionUpdatesPaginatedData';
import {
	prefetchLocalPagePaginatedRecentPetitionsDataFromApi,
	useLocalPagePaginatedRecentPetitionsDataFromApi,
} from './pageRecentPetitionsPaginatedData';
import {
	prefetchLocalPagePaginatedTrendingPetitionsDataFromApi,
	useLocalPagePaginatedTrendingPetitionsDataFromApi,
} from './pageTrendingPetitionsPaginatedData';
import {
	prefetchLocalPagePaginatedVictoriousPetitionsDataFromApi,
	useLocalPagePaginatedVictoriousPetitionsDataFromApi,
} from './pageVictoriousPetitionsPaginatedData';

export type LocalPagePrefetchedContext = Readonly<{
	pageData: NonNullable<Parameters<typeof useLocalPageDataFromApi>[0]>;
	petitionUpdatesPaginatedData: NonNullable<Parameters<typeof useLocalPagePaginatedPetitionUpdatesDataFromApi>[0]>;
	recentPetitionsPaginatedData: NonNullable<Parameters<typeof useLocalPagePaginatedRecentPetitionsDataFromApi>[0]>;
	trendingPetitionsPaginatedData: NonNullable<Parameters<typeof useLocalPagePaginatedTrendingPetitionsDataFromApi>[0]>;
	victoriousPetitionsPaginatedData: NonNullable<
		Parameters<typeof useLocalPagePaginatedVictoriousPetitionsDataFromApi>[0]
	>;
	decisionMakersPaginatedData: NonNullable<Parameters<typeof useLocalPagePaginatedDecisionMakersDataFromApi>[0]>;
}>;

export type LocalPageCombinedState = MergedState<
	ReturnType<typeof useLocalPageDataFromApi>,
	ReturnType<typeof useLocalPagePaginatedPetitionUpdatesDataFromApi>,
	ReturnType<typeof useLocalPagePaginatedRecentPetitionsDataFromApi>,
	ReturnType<typeof useLocalPagePaginatedTrendingPetitionsDataFromApi>,
	ReturnType<typeof useLocalPagePaginatedVictoriousPetitionsDataFromApi>,
	ReturnType<typeof useLocalPagePaginatedDecisionMakersDataFromApi>
>;

export function usePageContext(slug: string, prefetchedData?: LocalPagePrefetchedContext): LocalPageCombinedState {
	const query = useQueryString();
	return useMergedStates(
		useLocalPageDataFromApi(prefetchedData?.pageData, slug),
		useLocalPagePaginatedPetitionUpdatesDataFromApi(prefetchedData?.petitionUpdatesPaginatedData, slug),
		useLocalPagePaginatedRecentPetitionsDataFromApi(prefetchedData?.recentPetitionsPaginatedData, slug, {
			listTagsFilter: getQueryStringSingleValue(query, 'list_tags_filter'),
		}),
		useLocalPagePaginatedTrendingPetitionsDataFromApi(prefetchedData?.trendingPetitionsPaginatedData, slug, {
			listTagsFilter: getQueryStringSingleValue(query, 'list_tags_filter'),
		}),
		useLocalPagePaginatedVictoriousPetitionsDataFromApi(prefetchedData?.victoriousPetitionsPaginatedData, slug),
		useLocalPagePaginatedDecisionMakersDataFromApi(prefetchedData?.decisionMakersPaginatedData, slug),
	);
}

export async function prefetchPageContext(slug: string, context: PrefetchContext): Promise<LocalPagePrefetchedContext> {
	const [
		pageData,
		petitionUpdatesPaginatedData,
		recentPetitionsPaginatedData,
		trendingPetitionsPaginatedData,
		victoriousPetitionsPaginatedData,
		decisionMakersPaginatedData,
	] = await Promise.all([
		prefetchLocalPageDataFromApi(context, slug),
		prefetchLocalPagePaginatedPetitionUpdatesDataFromApi(context, slug),
		prefetchLocalPagePaginatedRecentPetitionsDataFromApi(context, slug, {
			listTagsFilter: getQueryStringSingleValue(context.query, 'list_tags_filter'),
		}),
		prefetchLocalPagePaginatedTrendingPetitionsDataFromApi(context, slug, {
			listTagsFilter: getQueryStringSingleValue(context.query, 'list_tags_filter'),
		}),
		prefetchLocalPagePaginatedVictoriousPetitionsDataFromApi(context, slug),
		prefetchLocalPagePaginatedDecisionMakersDataFromApi(context, slug),
	]);

	return {
		pageData,
		petitionUpdatesPaginatedData,
		recentPetitionsPaginatedData,
		trendingPetitionsPaginatedData,
		victoriousPetitionsPaginatedData,
		decisionMakersPaginatedData,
	};
}

export type LocalPagePrefetchedDataProps = PrefetchedDataProps<LocalPagePrefetchedContext>;
